import {
  HomeData,
  InvitationLanding,
  InvitationPreview,
  type ModelKeyResult,
  ModelKeyResult as ModelKeyResultSchema,
  ModelKeysList,
  MyInvitations,
  type PutRunnerAllowBody,
  type PutWorkspaceAllowBody,
  RunnerAllowList,
  WorkspaceAllowList,
} from "@minga/protocol"
import { queryOptions } from "@tanstack/react-query"
import { ApiError, apiJson, apiRequest } from "./api"
import { authClient, unwrap } from "./auth-client"
import { FORBIDDEN_TEXT } from "./query-client"

export type ModelProviderId = "anthropic" | "openai"

/**
 * The settings wave's HTTP routes (spec §6: H11–H17) and the Better Auth reads behind the settings pages. Responses
 * are decoded with the shared schemas. Model keys never enter a query key, a URL or a log: only the PUT body holds one.
 */

const enc = encodeURIComponent
const ws = (workspaceId: string) => `/api/workspaces/${enc(workspaceId)}`

// ---- H12 / H13: invitations ---------------------------------------------------------------------------------------

export const myInvitationsQuery = queryOptions({
  queryKey: ["me", "invitations"],
  queryFn: async () => (await apiJson("/api/me/invitations", MyInvitations)).invitations,
  staleTime: 30_000,
})

/**
 * The invite link's preview. Decision 0130: `key` is the link's `?k=` (the emailed link); with it the preview has the
 * invited address in full, so sign-up fills it in.
 */
export const invitationPreviewQuery = (invitationId: string, key?: string) =>
  queryOptions({
    queryKey: ["invitations", invitationId, "preview", key ?? null],
    queryFn: async (): Promise<InvitationPreview | null> => {
      try {
        return await apiJson(
          `/api/invitations/${enc(invitationId)}/preview${key ? `?k=${enc(key)}` : ""}`,
          InvitationPreview,
        )
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null
        throw error
      }
    },
    retry: false,
  })

/**
 * Decision 0130 (audit item 7): after Join, the project the invitation named (the first one the person is now in), or
 * null for workspace Home. Never throws: anything but an answer is Home.
 */
export const invitationLanding = async (
  invitationId: string,
): Promise<{ readonly workspaceId: string; readonly projectId: string | null } | null> => {
  try {
    return await apiJson(`/api/me/invitations/${enc(invitationId)}/landing`, InvitationLanding)
  } catch {
    return null
  }
}

/**
 * The same answer as a query, for an invite link opened again after it was accepted: where the person already is
 * (null when the invitation isn't theirs or they left).
 */
export const invitationLandingQuery = (invitationId: string) =>
  queryOptions({
    queryKey: ["invitations", invitationId, "landing"],
    queryFn: () => invitationLanding(invitationId),
    retry: false,
  })

// ---- H14: home --------------------------------------------------------------------------------------------------

export const homeQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "home"],
    queryFn: () => apiJson(`${ws(workspaceId)}/home`, HomeData),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

// ---- H15: model keys --------------------------------------------------------------------------------------------

export const modelKeysQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "model-keys"],
    queryFn: () => apiJson(`${ws(workspaceId)}/model-keys`, ModelKeysList),
    staleTime: 30_000,
  })

/** The outcome of "Test and save": stored, or a refusal the card explains (nothing stored). */
export type PutKeyOutcome =
  | { readonly kind: "saved"; readonly result: ModelKeyResult }
  | { readonly kind: "rejected" | "no_access" | "unreachable"; readonly httpStatus: number | null }

/** PUT …/model-keys/:provider. The key lives only in this request body. */
export const putModelKey = async (
  workspaceId: string,
  provider: ModelProviderId,
  key: string,
  force = false,
): Promise<PutKeyOutcome> => {
  try {
    const result = await apiJson(`${ws(workspaceId)}/model-keys/${provider}`, ModelKeyResultSchema, {
      method: "PUT",
      body: force ? { key, force: true } : { key },
    })
    return { kind: "saved", result }
  } catch (error) {
    if (error instanceof ApiError) {
      const test = error.body?.test as { httpStatus?: number | null } | undefined
      const httpStatus = typeof test?.httpStatus === "number" ? test.httpStatus : null
      if (error.body?.error === "key_rejected") return { kind: "rejected", httpStatus }
      if (error.body?.error === "key_no_access") return { kind: "no_access", httpStatus }
      if (error.body?.error === "test_unreachable") return { kind: "unreachable", httpStatus }
    }
    throw error
  }
}

export const testModelKey = (workspaceId: string, provider: ModelProviderId): Promise<ModelKeyResult> =>
  apiJson(`${ws(workspaceId)}/model-keys/${provider}/test`, ModelKeyResultSchema, { method: "POST" })

export const deleteModelKey = (workspaceId: string, provider: ModelProviderId) =>
  apiRequest(`${ws(workspaceId)}/model-keys/${provider}`, null, { method: "DELETE" })

// ---- decision 0096: the workspace's sandbox allowlist -----------------------------------------------------------

export const allowlistQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "allowlist"],
    queryFn: async () => (await apiJson(`${ws(workspaceId)}/allowlist`, WorkspaceAllowList)).entries,
    staleTime: 30_000,
  })

/** PUT …/allowlist: the whole list (owners and admins); entries already saved keep who added them. */
export const putAllowlist = async (workspaceId: string, body: PutWorkspaceAllowBody) =>
  (await apiJson(`${ws(workspaceId)}/allowlist`, WorkspaceAllowList, { method: "PUT", body })).entries

// ---- decision 0138: a runner owner's grants --------------------------------------------------------------------

export const runnerAllowlistQuery = (workspaceId: string, runnerId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "runners", runnerId, "allowlist"],
    queryFn: async () =>
      (await apiJson(`${ws(workspaceId)}/runners/${runnerId}/allowlist`, RunnerAllowList)).entries,
    staleTime: 30_000,
  })

/** PUT …/runners/:id/allowlist: the runner owner's whole list (only the owner). */
export const putRunnerAllowlist = async (workspaceId: string, runnerId: string, body: PutRunnerAllowBody) =>
  (
    await apiJson(`${ws(workspaceId)}/runners/${runnerId}/allowlist`, RunnerAllowList, {
      method: "PUT",
      body,
    })
  ).entries

/** Plain words for a failed model-key call. */
export const modelKeyErrorText = (error: Error): string => {
  if (error instanceof ApiError) {
    switch (error.body?.error) {
      case "rate_limited":
        return "Too many key checks. Wait a few minutes and try again."
      case "encryption_unavailable":
        return "Keys can't be stored right now: the server's encryption secret is missing."
      case "forbidden":
        return FORBIDDEN_TEXT
      case "no_key":
        return "There's no key for this provider any more."
      case "invalid_body":
        return "That doesn't look like an API key (20–512 characters, no spaces)."
    }
  }
  return error.message
}

// ---- Better Auth: members and invitations -------------------------------------------------------------------------

export const membersQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "members"],
    queryFn: async () =>
      unwrap(
        await authClient.organization.listMembers({ query: { organizationId: workspaceId, limit: 100 } }),
      ),
    staleTime: 60_000,
  })

export type MembersData = Awaited<ReturnType<NonNullable<ReturnType<typeof membersQuery>["queryFn"]>>>
export type WorkspaceMember = MembersData["members"][number]

/** Pending invitations (owner/admin only: the cloud refuses members). */
export const invitationsQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ["workspaces", workspaceId, "invitations"],
    queryFn: async () =>
      unwrap(await authClient.organization.listInvitations({ query: { organizationId: workspaceId } })),
    staleTime: 30_000,
  })

export type WorkspaceInvitation = NonNullable<
  Awaited<ReturnType<NonNullable<ReturnType<typeof invitationsQuery>["queryFn"]>>>
>[number]

// ---- Better Auth: account -----------------------------------------------------------------------------------------

export const accountsQuery = queryOptions({
  queryKey: ["account", "accounts"],
  queryFn: async () => unwrap(await authClient.listAccounts()),
  staleTime: 60_000,
})

export const passkeysQuery = queryOptions({
  queryKey: ["account", "passkeys"],
  queryFn: async () => unwrap(await authClient.passkey.listUserPasskeys()),
  staleTime: 60_000,
})

export const sessionsQuery = queryOptions({
  queryKey: ["account", "sessions"],
  queryFn: async () => unwrap(await authClient.listSessions()),
  staleTime: 30_000,
})
